import { observer } from 'mobx-react';
import * as React from 'react';
import { EquipmentAssignmentsService, IEquipmentAssignment } from '@shared/services/EquipmentAssignmentsService';
import { makeObservable, observable } from 'mobx';
import { cs } from '@shared/utils/utils-classname';
import { Button } from '@shared/ui-react/Button';
import { DialogUtils } from '@shared/ui-react/Dialog';
import { Notify } from '@shared/ui-native/Notify';
import { VehiclesSelectTable } from '@shared/vehicles/VehiclesSelectTable';
import { FileTreeService } from '@shared/services/FileTreeService';
import { AssignmentFilesSelector } from '@shared/vehicles/assignments/AssignmentFilesSelector';
import { CollapsiblePanel } from '@shared/ui-react/CollapsiblePanel';


export const AssignmentCreateEditDialog = {
    open: (item?: IEquipmentAssignment, vehicleUuid?: string, withFiles?: boolean, onSuccess?: Function) => {
        DialogUtils.renderComponentBasic(__('js_vehicles.assignments.assignment'),
            <AssignmentEditDialogComponent assignment={item} onSuccess={onSuccess} withFiles={withFiles} vehicleUuid={vehicleUuid}/>);
    },
    clone: (item: IEquipmentAssignment, onSuccess?: Function) => {
        const clone = JSON.parse(JSON.stringify(item));
        delete clone.uuid;
        delete clone.name;
        DialogUtils.renderComponentBasic(__('js_vehicles.assignments.assignment'),
            <AssignmentEditDialogComponent assignment={clone} onSuccess={onSuccess} withFiles={true} vehicleUuid={null}/>);
    }
}

@observer
class AssignmentEditDialogComponent extends React.Component<{
    assignment?: IEquipmentAssignment,
    vehicleUuid: string,
    withFiles: boolean,
    onSuccess: Function,
}> {
    @observable isLoading = false;
    @observable nameInput = '';
    @observable vehicleUuids: string[] = [];
    @observable fileUuids: string[] = [];
    @observable.ref private treeData: ITreeData;

    get hasChanged() {
        return this.fileUuids.slice().sort().join() !== this.props.assignment?.documents?.slice().sort().join() ||
            this.vehicleUuids.slice().sort().join() !== this.props.assignment?.equipment?.slice().sort().join();
    }

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        FileTreeService.fetchFileTree(false).then(treeData => {
            this.treeData = treeData;

            if (this.props.assignment) {
                if (this.props.assignment?.uuid) {
                    this.nameInput = this.props.assignment.name || '';
                }
                this.fileUuids = this.props.assignment.documents ?? [];
                this.vehicleUuids = this.props.assignment.equipment ?? [];
            } else {
                if (this.props.vehicleUuid) {
                    this.vehicleUuids = [this.props.vehicleUuid];
                }
            }
        });
    }

    render() {
        if (!this.treeData) {
            return <div style={{ height: 300 }} className='overlay-loading'></div>;
        }

        return (<div id='dialog-copy-rights' style={{ width: 600 }}
                     className={cs('flex-column of-h', { 'overlay-loading': this.isLoading })}>

            <div className='card padded flex-column of-h gap'>
                <div className="flex-column">
                    <h6 style={{ fontSize: 13, marginBottom: 8 }}>{__('general.name')}</h6>
                    <input type='text'
                           style={{ width: '250px', fontSize: 13 }}
                           maxLength={100}
                           value={this.nameInput}
                           autoFocus={true}
                           onChange={e => this.nameInput = e.currentTarget.value}/>
                </div>

                <CollapsiblePanel label={() => <span>{__('js_vehicles.assignments.assign_to_vehicles')}: {this.vehicleUuids?.length}</span>} defaultIsOpen={true} animated={false}>
                    <VehiclesSelectTable checkedUuids={this.vehicleUuids}
                                         onChange={e => this.vehicleUuids = e}/>
                </CollapsiblePanel>

                {this.props.withFiles && <CollapsiblePanel label={() => <span>{__('general.files')}: {this.fileUuids?.length}</span>} defaultIsOpen={true} animated={false} baseClassName='flex-column of-h'>
                    <div className='of-a' style={{ minHeight: 200 }}>
                        <AssignmentFilesSelector
                            treeData={this.treeData}
                            checkedUuids={this.fileUuids}
                            onChange={uuids => this.fileUuids = uuids}
                        />
                    </div>
                </CollapsiblePanel>}
            </div>

            <footer className='padded flex-row gap-half' style={{ flexShrink: 0 }}>
                <Button onClick={this.cancel} style={{ marginRight: 'auto' }}>{__('general.cancel')}</Button>
                { this.props.assignment &&
                    <Button onClick={this.reset} icon='iki iki-arrow-reset' disabled={!this.hasChanged}>{__('js_vehicles.assignments.reset_button')}</Button>
                }
                <Button className='ik-button-primary'  onClick={this.save} disabled={this.isLoading || !this.isValid}>{__('general.save')}</Button>
            </footer>
        </div>);
    }

    cancel = () => {
        DialogUtils.close();
    };

    save = async () => {
        this.isLoading = true;
        try {
            if (this.props.assignment?.uuid) {
                const newAssignment = JSON.parse(JSON.stringify(this.props.assignment));

                if (this.props.assignment.name !== this.nameInput) {
                    await EquipmentAssignmentsService.updateName(this.props.assignment.uuid, this.nameInput);
                    newAssignment.name = this.nameInput;
                }
                await EquipmentAssignmentsService.saveFullEquipment(this.props.assignment.uuid, this.props.assignment.equipment, this.vehicleUuids);
                newAssignment.equipment = this.vehicleUuids;

                if (this.props.withFiles) {
                    await EquipmentAssignmentsService.saveFullAssignments(this.props.assignment.uuid, this.props.assignment.documents, this.fileUuids);
                    newAssignment.documents = this.fileUuids;
                }

                this.props.onSuccess(newAssignment);
            }
            else {
                const created = await EquipmentAssignmentsService.create(this.nameInput);

                if (this.vehicleUuids.length) {
                    await EquipmentAssignmentsService.addEquipmentToAssignment(created.assignment.uuid, this.vehicleUuids);
                    created.assignment.equipment = this.vehicleUuids;
                }

                if (this.props.withFiles && this.fileUuids.length) {
                    await EquipmentAssignmentsService.addModelsToAssignment(created.assignment.uuid, this.fileUuids);
                    created.assignment.documents = this.fileUuids;
                }
                this.props.onSuccess(created.assignment);
            }

            Notify.success();
            this.cancel();
        } catch {
            Notify.error('Failed to update assignment');
            this.isLoading = false;
        }
    };

    reset = () => {
        if (this.props.assignment?.uuid) {
            this.nameInput = this.props.assignment.name || '';
        }
        this.fileUuids = [...this.props.assignment.documents];
        this.vehicleUuids = [...this.props.assignment.equipment];
    }

    get isValid() {
        return this.nameInput?.length > 0;
    }
}
